import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CUIText, Icon, IconNames } from '@dotcom/crown-ui';
import { useInitializationDataContext } from '../../../context/data-context';
import { EnvelopeType } from '../../../global-types/envelope';
import { navigateTo } from '../../../utils/dom';
import { getLoginUrl, isLoggedIn } from '../../../utils/login';
import { useEnvelopeSelection } from '../../../views/select-envelope/hooks/use-envelope-selection';
import { AddressBookSignInModal } from '../../address-book-sign-in-modal/address-book-sign-in-modal';
import { AddressBookButtonProps } from '../address-form-types';
import styles from '../address-form.module.scss';

export const AddressBookButton = ({ click }: AddressBookButtonProps): React.ReactElement => {
  const {
    initializedDataState: { data: initializedData, lineItemUUID },
  } = useInitializationDataContext();
  const { selectedEnvelope } = useEnvelopeSelection();
  const isReturnRecipient = selectedEnvelope === EnvelopeType.ReturnRecipient;
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

  const { t } = useTranslation();
  const loginRedirectUrl = getLoginUrl(
    initializedData?.project_id as string,
    initializedData?.account_id as string,
    lineItemUUID,
  );

  // onClick handles the redirect to login page if user is not logged in,
  // otherwise, it calls the click function passed as a prop which should open the address book
  const onClick = () => {
    if (isLoggedIn()) {
      click();
    } else {
      setIsSignInModalOpen(true);
    }
  };

  const handleSignInButton = () => {
    navigateTo(loginRedirectUrl);
  };

  const handleCloseDialog = () => {
    setIsSignInModalOpen(false);
  };

  return (
    <>
      {isSignInModalOpen && (
        <AddressBookSignInModal
          isOpen={isSignInModalOpen}
          handleCloseDialog={handleCloseDialog}
          handleSignInButton={handleSignInButton}
          title={t('AddressBookSignInModal.AddressBookSignInModalTitle')}
        />
      )}
      <button
        onClick={onClick}
        className={styles['open-address-book-button']}
        type="button"
        data-testid="address-book-button"
        aria-label="AddressBookButton"
      >
        <div className={styles['open-address-book-button-container']}>
          <div className={styles['open-address-book-button-icon-container']}>
            <Icon size={'40'} name={IconNames.AddressBookBold} />
          </div>
          <div className={styles['open-address-book-button-text-container']}>
            <CUIText as="div" color="textPrimary" weight="bold" data-testid="address-book-title">
              {t('AddressBookButton.addressBookTitle')}
            </CUIText>
            <CUIText
              as="div"
              className={styles['open-address-book-button-text-label']}
              data-testid="address-book-label"
            >
              {isReturnRecipient
                ? t('AddressBookButton.addressBookReturnRecipientLabel')
                : t('AddressBookButton.addressBookLabel')}
            </CUIText>
          </div>
          <div className={styles['open-address-book-caret-container']}>
            <Icon color={'black'} size={'16'} name={IconNames.ArrowsCaretUp} />
          </div>
        </div>
      </button>
    </>
  );
};
